import { Link } from 'react-router-dom';
import { FilePlus2, RefreshCw, AlertTriangle, Clock, Truck, PhoneCall } from 'lucide-react';
import { useCommercialDashboard } from '@/hooks/useCommercial';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Accueil « Aujourd'hui » du commercial.
 *
 * Vue poussée : on liste ce qui l'attend, avec des chemins d'action clairs.
 * Pas de CRUD ici, uniquement des tuiles-action.
 */
export const AccueilPage = () => {
  const { data, isLoading, refetch, isFetching } = useCommercialDashboard();

  const tiles = [
    {
      key: 'renvoyees', to: '/commercial/proformas?statut=renvoyees',
      icon: <AlertTriangle size={22} />,
      tone: 'danger',
      titre: 'À corriger',
      valeur: data?.renvoyeesACorriger ?? 0,
      sous: 'renvoyées par le DAF',
    },
    {
      key: 'validation', to: '/commercial/proformas?statut=en_validation_daf',
      icon: <Clock size={22} />,
      tone: 'info',
      titre: 'Chez le DAF',
      valeur: data?.enValidationDaf ?? 0,
      sous: 'en attente de validation',
    },
    {
      key: 'relance', to: '/commercial/proformas?statut=devis',
      icon: <RefreshCw size={22} />,
      tone: 'warning',
      titre: 'À relancer',
      valeur: data?.proformasARelancer ?? 0,
      sous: 'proformas > 7j sans réponse',
    },
    {
      key: 'commandes', to: '/commercial/commandes',
      icon: <Truck size={22} />,
      tone: 'success',
      titre: 'Commandes en cours',
      valeur: data?.commandesEnCours ?? 0,
      sous: 'confirmées, en fab, livrées…',
    },
    {
      key: 'prospects', to: '/commercial/prospects',
      icon: <PhoneCall size={22} />,
      tone: 'neutral',
      titre: 'Prospects',
      valeur: data?.prospectsARecontacter ?? 0,
      sous: 'à recontacter aujourd\'hui',
    },
  ] as const;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Aujourd'hui</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>
            Ce qui t'attend en priorité
          </p>
        </div>
        <button
          onClick={() => void refetch()}
          className="ds-btn ds-btn-g ds-btn-sm"
          title="Rafraîchir"
          disabled={isFetching}
          style={{
            border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8, background: '#fff',
            padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      {/* Grande action principale : Nouvelle proforma */}
      <Link
        to="/commercial/nouvelle"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          background: 'var(--pr-1, #1F4E79)', color: '#fff',
          padding: '18px 20px', borderRadius: 14,
          fontWeight: 700, fontSize: 16, marginBottom: 20,
          boxShadow: '0 8px 24px -8px rgba(31,78,121,0.5)',
          textDecoration: 'none',
        }}
      >
        <FilePlus2 size={20} /> Nouvelle proforma
      </Link>

      {/* Tuiles compteurs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
        {tiles.map((t) => (
          <Link
            key={t.key}
            to={t.to}
            style={{
              background: '#fff', border: `1px solid ${toneBorder(t.tone)}`,
              borderRadius: 12, padding: 16, textDecoration: 'none',
              color: 'inherit', display: 'flex', flexDirection: 'column', gap: 6,
              boxShadow: '0 2px 4px -2px rgba(0,0,0,0.05)',
              minHeight: 110,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: toneColor(t.tone) }}>
              {t.icon}
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2, #374151)' }}>{t.titre}</span>
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: toneColor(t.tone) }}>
              {isLoading ? '…' : t.valeur}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>{t.sous}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

function toneColor(t: string): string {
  switch (t) {
    case 'danger':  return '#B91C1C';
    case 'warning': return '#B45309';
    case 'info':    return '#1E40AF';
    case 'success': return '#047857';
    default:        return '#374151';
  }
}
function toneBorder(t: string): string {
  switch (t) {
    case 'danger':  return '#FECACA';
    case 'warning': return '#FDE68A';
    case 'info':    return '#BFDBFE';
    case 'success': return '#A7F3D0';
    default:        return '#E5E7EB';
  }
}
